import {
  useMemo,
  useState,
  type ComponentType,
  type ReactNode,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, Loader2, MoreHorizontal } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { readEdgeError } from "@/lib/edgeError";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export interface ConnectionField {
  key: string;
  label: string;
  placeholder: string;
  secret?: boolean;
  optional?: boolean;
  help?: string;
}

interface Props {
  className?: string;
  channelName: string;
  functionName: string;
  queryKey: string;
  Icon: ComponentType<{ className?: string; size?: number }>;
  iconBackground: string;
  disconnectedDescription: string;
  dialogDescription: string;
  fields: ConnectionField[];
  instructions: ReactNode;
  connectedLabel: (state: Record<string, unknown>) => string;
  connectedDetails?: (state: Record<string, unknown>) => ReactNode;
}

export default function CredentialMessagingConnectionCard({
  className,
  channelName,
  functionName,
  queryKey,
  Icon,
  iconBackground,
  disconnectedDescription,
  dialogDescription,
  fields,
  instructions,
  connectedLabel,
  connectedDetails,
}: Props) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [setupOpen, setSetupOpen] = useState(false);
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [busy, setBusy] = useState<"connect" | "disconnect" | null>(null);
  const initialValues = useMemo(
    () => Object.fromEntries(fields.map((field) => [field.key, ""])),
    [fields],
  );
  const [values, setValues] = useState<Record<string, string>>(initialValues);

  const { data: state, isLoading } = useQuery({
    queryKey: [queryKey, user?.id],
    enabled: !!user,
    staleTime: 15_000,
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke(functionName, {
        method: "GET",
      });
      if (error) throw error;
      return (data ?? {}) as Record<string, unknown>;
    },
  });

  const connected = state?.connected === true;
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: [queryKey, user?.id] });
  const requiredComplete = fields
    .filter((field) => !field.optional)
    .every((field) => values[field.key]?.trim());

  const connect = async () => {
    if (!requiredComplete) {
      toast.error(`Complete the required ${channelName} fields first.`);
      return;
    }
    setBusy("connect");
    try {
      const body = Object.fromEntries(
        Object.entries(values)
          .map(([key, value]) => [key, value.trim()])
          .filter(([, value]) => !!value),
      );
      const { data, error } = await supabase.functions.invoke(functionName, {
        body: { action: "connect_manual", ...body },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.detail || data.error);
      toast.success(`${channelName} connected.`);
      setValues(initialValues);
      await refresh();
    } catch (error) {
      console.error(`${channelName} connect failed:`, error);
      toast.error(await readEdgeError(error, `Couldn't connect ${channelName}.`));
    } finally {
      setBusy(null);
    }
  };

  const disconnect = async () => {
    setBusy("disconnect");
    try {
      const { data, error } = await supabase.functions.invoke(functionName, {
        body: { action: "disconnect" },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.detail || data.error);
      toast.success(`${channelName} disconnected.`);
      setDisconnectOpen(false);
      setSetupOpen(false);
      await refresh();
    } catch (error) {
      toast.error(await readEdgeError(error, `Couldn't disconnect ${channelName}.`));
    } finally {
      setBusy(null);
    }
  };

  const copyWebhook = async () => {
    const webhook = typeof state?.webhook_url === "string" ? state.webhook_url : "";
    if (!webhook) return;
    await navigator.clipboard.writeText(webhook);
    toast.success("Webhook URL copied.");
  };

  return (
    <>
      <div className={`flex items-center justify-between gap-4 rounded-xl border bg-card p-4 shadow-sm ${className ?? ""}`}>
        <div className="flex min-w-0 items-center gap-4">
          <div
            className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg"
            style={{ backgroundColor: iconBackground }}
          >
            <Icon className="h-6 w-6" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold">{channelName}</h3>
              {connected && (
                <Badge className="border-transparent bg-green-600 text-white hover:bg-green-600">
                  Connected
                </Badge>
              )}
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              {disconnectedDescription}
            </p>
          </div>
        </div>

        {isLoading ? (
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        ) : (
          <div className="flex shrink-0 items-center gap-3">
            <Switch
              aria-label={
                connected
                  ? `Disconnect ${channelName}`
                  : `Connect ${channelName}`
              }
              checked={connected}
              disabled={busy !== null}
              onCheckedChange={(checked) =>
                checked ? setSetupOpen(true) : setDisconnectOpen(true)}
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  aria-label={`${channelName} connection options`}
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                >
                  <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {connected && (
                  <>
                    <DropdownMenuLabel className="font-normal text-muted-foreground">
                      {connectedLabel(state ?? {})}
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                  </>
                )}
                <DropdownMenuItem onClick={() => setSetupOpen(true)}>
                  {connected ? "Manage connection" : `Connect ${channelName}`}
                </DropdownMenuItem>
                {connected && (
                  <DropdownMenuItem
                    className="text-destructive"
                    onClick={() => setDisconnectOpen(true)}
                  >
                    Disconnect
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>

      <Dialog open={setupOpen} onOpenChange={setSetupOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>
              {connected ? `${channelName} connection` : `Connect ${channelName}`}
            </DialogTitle>
            <DialogDescription>{dialogDescription}</DialogDescription>
          </DialogHeader>

          {!connected ? (
            <div className="space-y-5">
              <div className="rounded-lg border bg-muted/30 p-4 text-sm">
                {instructions}
              </div>
              {typeof state?.webhook_url === "string" && (
                <div className="space-y-2">
                  <Label>Webhook URL</Label>
                  <div className="flex gap-2">
                    <Input
                      readOnly
                      value={state.webhook_url}
                      className="font-mono text-xs"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={copyWebhook}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
              {fields.map((field) => (
                <div className="space-y-2" key={field.key}>
                  <Label htmlFor={`${queryKey}-${field.key}`}>
                    {field.label}
                    {field.optional ? " (optional)" : ""}
                  </Label>
                  <Input
                    id={`${queryKey}-${field.key}`}
                    name={`${queryKey}-${field.key}`}
                    type={field.secret ? "password" : "text"}
                    placeholder={field.placeholder}
                    value={values[field.key] ?? ""}
                    onChange={(event) =>
                      setValues((current) => ({
                        ...current,
                        [field.key]: event.target.value,
                      }))}
                    autoComplete={field.secret ? "new-password" : "off"}
                    data-1p-ignore
                    data-form-type="other"
                    data-lpignore="true"
                  />
                  {field.help && (
                    <p className="text-xs text-muted-foreground">{field.help}</p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-4 text-sm">
              {connectedDetails?.(state ?? {})}
              {typeof state?.webhook_url === "string" && (
                <div className="space-y-2">
                  <Label>Webhook URL</Label>
                  <div className="flex gap-2">
                    <Input
                      readOnly
                      value={state.webhook_url}
                      className="font-mono text-xs"
                    />
                    <Button variant="outline" size="icon" onClick={copyWebhook}>
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setSetupOpen(false)}>
              Close
            </Button>
            {!connected && (
              <Button
                onClick={connect}
                disabled={busy === "connect" || !requiredComplete}
              >
                {busy === "connect" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Connect {channelName}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={disconnectOpen} onOpenChange={setDisconnectOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect {channelName}?</AlertDialogTitle>
            <AlertDialogDescription>
              Dibs will stop responding on this channel. Existing conversations
              remain in the inbox and you can reconnect later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy === "disconnect"}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={busy === "disconnect"}
              onClick={(event) => {
                event.preventDefault();
                disconnect();
              }}
            >
              {busy === "disconnect" && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Disconnect
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
